import React, { Suspense, useState } from 'react';

import { DropdownMenu } from '@buildoutinc/blueprint-react/ui/DropdownMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faHandshake, faPlus, faSquareCheck, faUser } from '@fortawesome/pro-regular-svg-icons';

import ErrorBoundary from 'components/error_boundary';
import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { OsNavCreateOption } from './types';

// The nav ships with every page, so each create flow is its own chunk, loaded when it is picked.
const FLOWS = {
  contact: React.lazy(() => import('./create_flows/contact')),
  deal: React.lazy(() => import('./create_flows/deal')),
  property: React.lazy(() => import('./create_flows/property')),
  task: React.lazy(() => import('./create_flows/task')),
};

const ICONS: Record<OsNavCreateOption['key'], IconDefinition> = {
  contact: faUser,
  deal: faHandshake,
  property: faBuilding,
  task: faSquareCheck,
};

interface Props {
  options: OsNavCreateOption[];
}

function Flow({ option, onClose }: { option: OsNavCreateOption; onClose: () => void }) {
  switch (option.key) {
  case 'contact': return <FLOWS.contact option={option} onClose={onClose} />;
  case 'deal': return <FLOWS.deal option={option} onClose={onClose} />;
  case 'property': return <FLOWS.property option={option} onClose={onClose} />;
  case 'task': return <FLOWS.task option={option} onClose={onClose} />;
  default: return null;
  }
}

export default function CreateMenu({ options }: Props) {
  const [active, setActive] = useState<OsNavCreateOption | null>(null);

  // A flow that throws, or whose chunk fails to load (e.g. a tab left open across a deploy), must not take
  // the header down with it: it closes, and the user is told.
  const failFlow = () => {
    showSnackbar({ text: 'Something went wrong. Please try again.', variant: 'danger' });
    setActive(null);
  };

  return (
    <span className="os-nav__action-wrap d-none d-md-inline-flex">
      <DropdownMenu>
        <DropdownMenu.Trigger
          render={<Button aria-label="New" className="os-nav__action" icon={faPlus} variant="ghost" />}
        />
        <DropdownMenu.Content align="end" sideOffset={8}>
          {options.map((option) => {
            const content = <><FontAwesomeIcon fixedWidth icon={ICONS[option.key]} /> {option.label}</>;

            // Without Insights, New Property is the property form itself, as on the properties page.
            if (option.key === 'property' && !option.insights) {
              return (
                <DropdownMenu.Item key={option.key} render={<a href={option.newPropertyPath} />}>
                  {content}
                </DropdownMenu.Item>
              );
            }

            return <DropdownMenu.Item key={option.key} onClick={() => setActive(option)}>{content}</DropdownMenu.Item>;
          })}
        </DropdownMenu.Content>
      </DropdownMenu>

      {active && (
        <ErrorBoundary fallback={null} key={active.key} onError={failFlow}>
          <Suspense fallback={null}>
            <Flow option={active} onClose={() => setActive(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
    </span>
  );
}
